import { useCallback, useEffect, useRef, useState } from "react";
import type Lenis from "lenis";
import { useDocumentReady } from "@/hooks/use-document-ready";

const NAV_OFFSET = 80;

export type SectionId = "home" | "about" | "stack" | "projects" | "roadmap" | "contact";

type SectionProgress = {
  activeId: SectionId;
  progress: number;
};

type SectionMetrics = {
  top: number;
  height: number;
};

type SectionSegment = {
  id: SectionId;
  start: number;
  end: number;
};

function measureSections(sectionIds: SectionId[], scrollY: number): Map<SectionId, SectionMetrics> {
  const metrics = new Map<SectionId, SectionMetrics>();

  for (const id of sectionIds) {
    const el = document.getElementById(id);
    if (!el) continue;

    metrics.set(id, {
      top: el.getBoundingClientRect().top + scrollY,
      height: el.offsetHeight,
    });
  }

  return metrics;
}

function getMaxScrollY(viewportHeight: number): number {
  if (typeof document === "undefined") return 0;
  return Math.max(document.documentElement.scrollHeight - viewportHeight, 0);
}

function isProjectsHijack(
  id: SectionId,
  section: SectionMetrics | undefined,
  viewportHeight: number,
): boolean {
  return (
    id === "projects" && viewportHeight > 0 && !!section && section.height > viewportHeight * 1.25
  );
}

function buildSectionSegments(
  sectionIds: SectionId[],
  metrics: Map<SectionId, SectionMetrics>,
  viewportHeight: number,
): SectionSegment[] {
  const segments: SectionSegment[] = [];

  for (let index = 0; index < sectionIds.length; index++) {
    const id = sectionIds[index];
    const section = metrics.get(id);
    if (!section) continue;

    const nextId = sectionIds[index + 1];
    const next = nextId ? metrics.get(nextId) : undefined;
    const hijack = isProjectsHijack(id, section, viewportHeight);
    const nextIsHijack = isProjectsHijack(nextId, next, viewportHeight);

    const start = id === "home" ? 0 : hijack ? section.top : section.top - NAV_OFFSET;

    let end: number;
    if (hijack) {
      // Stay active through the manifesto divider until Contact takes over.
      end = next ? next.top - NAV_OFFSET : section.top + section.height - NAV_OFFSET;
    } else if (nextIsHijack && next) {
      end = next.top;
    } else if (next) {
      end = next.top - NAV_OFFSET;
    } else {
      end = section.top + section.height;
    }

    segments.push({
      id,
      start,
      end: Math.max(end, start + 1),
    });
  }

  return segments;
}

function computeSectionProgress(
  sectionIds: SectionId[],
  scrollY: number,
  metrics: Map<SectionId, SectionMetrics>,
): SectionProgress {
  const viewportHeight = typeof window !== "undefined" ? window.innerHeight : 0;
  const segments = buildSectionSegments(sectionIds, metrics, viewportHeight);
  const maxScrollY = getMaxScrollY(viewportHeight);
  const lastSegment = segments[segments.length - 1];

  if (segments.length === 0) {
    return { activeId: sectionIds[0], progress: 0 };
  }

  for (let index = 0; index < segments.length; index++) {
    const segment = segments[index];

    if (scrollY >= segment.start && scrollY < segment.end) {
      const section = metrics.get(segment.id);
      const hijack = isProjectsHijack(segment.id, section, viewportHeight);
      const isLast = segment.id === lastSegment?.id;

      let progress: number;
      if (hijack && section) {
        const hijackEnd = section.top + section.height - viewportHeight;
        const hijackRange = Math.max(hijackEnd - segment.start, 1);
        progress = Math.min(Math.max((scrollY - segment.start) / hijackRange, 0), 1);
      } else if (isLast) {
        const progressEnd = Math.max(maxScrollY, segment.start + 1);
        progress = Math.min(
          Math.max((scrollY - segment.start) / (progressEnd - segment.start), 0),
          1,
        );
      } else {
        const range = segment.end - segment.start;
        progress = Math.min(Math.max((scrollY - segment.start) / range, 0), 1);
      }

      return { activeId: segment.id, progress };
    }
  }

  if (lastSegment && scrollY >= lastSegment.start) {
    return { activeId: lastSegment.id, progress: 1 };
  }

  return { activeId: segments[0].id, progress: 0 };
}

export function useActiveSection(sectionIds: SectionId[], lenis: Lenis | null) {
  const documentReady = useDocumentReady();
  const metricsRef = useRef<Map<SectionId, SectionMetrics>>(new Map());
  const rafRef = useRef(0);
  const [state, setState] = useState<SectionProgress>(() => ({
    activeId: sectionIds[0],
    progress: 0,
  }));

  const getScrollY = useCallback(() => {
    return lenis?.scroll ?? window.scrollY;
  }, [lenis]);

  const refreshMetrics = useCallback(() => {
    metricsRef.current = measureSections(sectionIds, getScrollY());
  }, [getScrollY, sectionIds]);

  const update = useCallback(() => {
    const scrollY = getScrollY();
    const next = computeSectionProgress(sectionIds, scrollY, metricsRef.current);
    setState(next);
  }, [getScrollY, sectionIds]);

  const scheduleUpdate = useCallback(() => {
    if (rafRef.current) return;

    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      update();
    });
  }, [update]);

  useEffect(() => {
    if (!documentReady) return;

    refreshMetrics();
    update();

    const onResize = () => {
      refreshMetrics();
      update();
    };

    const resizeObserver = new ResizeObserver(() => {
      refreshMetrics();
      scheduleUpdate();
    });

    for (const id of sectionIds) {
      const el = document.getElementById(id);
      if (el) resizeObserver.observe(el);
    }

    if (lenis) {
      lenis.on("scroll", scheduleUpdate);
      window.addEventListener("resize", onResize);

      return () => {
        lenis.off("scroll", scheduleUpdate);
        window.removeEventListener("resize", onResize);
        resizeObserver.disconnect();
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
      };
    }

    window.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", onResize);
      resizeObserver.disconnect();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [documentReady, lenis, update, refreshMetrics, scheduleUpdate, sectionIds]);

  return state;
}
